import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Trophy, Medal, Award, Star, TrendingUp, Calendar,
  Crown, Flame, Eye, Heart, ChevronRight, Clock
} from 'lucide-react';
import { getCurrentWeekPrizes, getComics } from '../services/api';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';

// Helper function for rank badge styling
const getRankBadgeStyle = (index) => {
  if (index === 0) return 'bg-[#FFB703] text-black';
  if (index === 1) return 'bg-gray-300 text-black';
  if (index === 2) return 'bg-amber-600 text-white';
  return 'bg-[var(--surface)] text-[var(--text-secondary)]';
};

const PRIZE_CATEGORIES = [
  { 
    id: 'most_views', 
    name: 'Most Views', 
    icon: Eye, 
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    prize: 100,
    description: 'Comic with the highest view count this week'
  },
  { 
    id: 'most_likes', 
    name: 'Most Liked', 
    icon: Heart, 
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    prize: 75,
    description: 'Comic with the most likes this week'
  },
  { 
    id: 'rising_star', 
    name: 'Rising Star', 
    icon: TrendingUp, 
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    prize: 50,
    description: 'Fastest growing comic this week'
  },
  { 
    id: 'editors_pick', 
    name: "Editor's Pick", 
    icon: Award, 
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    prize: 50,
    description: 'Selected by our editorial team for excellence'
  },
];

export function WeeklyPrizesBanner() {
  const [prizes, setPrizes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    fetchPrizes();
    updateTimeLeft();
    const timer = setInterval(updateTimeLeft, 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchPrizes = async () => {
    try {
      const res = await getCurrentWeekPrizes();
      setPrizes(res.data);
    } catch (error) {
      console.error('Error fetching prizes:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTimeLeft = () => {
    const now = new Date();
    const endOfWeek = new Date();
    endOfWeek.setDate(now.getDate() + (7 - now.getDay()));
    endOfWeek.setHours(23, 59, 59, 999);
    
    const diff = endOfWeek - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    setTimeLeft(`${days}d ${hours}h`);
  };

  return (
    <div className="bg-gradient-to-r from-[#E63946]/20 via-[#FFB703]/20 to-[#E63946]/20 border border-[#FFB703]/30 rounded-xl p-6 mb-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[#FFB703]/20 flex items-center justify-center">
            <Trophy className="text-[#FFB703]" size={28} />
          </div>
          <div>
            <h3 className="font-teko text-2xl">Weekly Prizes</h3>
            <p className="text-sm text-[var(--text-secondary)]">
              Total prize pool: <span className="text-[#FFB703] font-bold">${prizes?.total_pool || 275}</span>
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">Time Left</p>
            <p className="font-teko text-2xl text-[var(--primary)]">{timeLeft}</p>
          </div>
          
          <Link 
            to="/prizes"
            className="px-6 py-2 bg-[#FFB703] text-black font-bold rounded-lg hover:bg-[#FFB703]/90 transition-colors flex items-center gap-2"
          >
            View Prizes <ChevronRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  );
}

export function PrizeLeaderboard() {
  const [topComics, setTopComics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('most_views');

  useEffect(() => {
    fetchTopComics();
  }, [selectedCategory]);

  const fetchTopComics = async () => {
    try {
      setLoading(true);
      const sortField = selectedCategory === 'most_views' ? 'views' : 
                       selectedCategory === 'most_likes' ? 'likes' : 'created_at';
      const res = await getComics({ sort: sortField, order: 'desc', limit: 10 });
      setTopComics(res.data);
    } catch (error) {
      console.error('Error fetching top comics:', error);
    } finally {
      setLoading(false);
    }
  };

  const category = PRIZE_CATEGORIES.find(c => c.id === selectedCategory);

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6" data-testid="prize-leaderboard">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-teko text-2xl">Prize Leaderboard</h3>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm"
        >
          {PRIZE_CATEGORIES.slice(0, 3).map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {/* Category Info */}
      {category && (
        <div className={`flex items-center gap-3 p-4 ${category.bgColor} rounded-lg mb-6`}>
          <category.icon className={category.color} size={24} />
          <div>
            <p className="font-medium">{category.name} - ${category.prize} Prize</p>
            <p className="text-sm text-[var(--text-secondary)]">{category.description}</p>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="space-y-3">
        {loading ? (
          [...Array(5)].map((_, i) => (
            <div key={`leaderboard-skeleton-${i}`} className="flex items-center gap-4 p-3 bg-[var(--background)] rounded-lg animate-pulse">
              <div className="w-8 h-8 bg-[var(--border)] rounded-full" />
              <div className="w-12 h-16 bg-[var(--border)] rounded" />
              <div className="flex-1">
                <div className="h-4 w-1/2 bg-[var(--border)] rounded mb-2" />
                <div className="h-3 w-1/3 bg-[var(--border)] rounded" />
              </div>
            </div>
          ))
        ) : (
          topComics.map((comic, index) => (
            <Link
              key={comic.id}
              to={`/comic/${comic.id}`}
              className="flex items-center gap-4 p-3 bg-[var(--background)] rounded-lg hover:bg-[var(--primary)]/5 transition-colors group"
              data-testid={`leaderboard-${index}`}
            >
              {/* Rank */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${getRankBadgeStyle(index)}`}
              >
                {index + 1}
              </div>

              {/* Cover */}
              <img 
                src={comic.cover_image} 
                alt={comic.title}
                className="w-12 h-16 object-cover rounded"
              />

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium line-clamp-1 group-hover:text-[var(--primary)] transition-colors">
                  {comic.title}
                </p>
                <p className="text-sm text-[var(--text-secondary)]">
                  by {comic.creator_name}
                </p>
              </div>

              {/* Stats */}
              <div className="text-right">
                <p className="font-bold text-lg">
                  {selectedCategory === 'most_views' ? comic.views?.toLocaleString() : 
                   selectedCategory === 'most_likes' ? comic.likes?.toLocaleString() : 
                   comic.views?.toLocaleString()}
                </p>
                <p className="text-xs text-[var(--text-secondary)]">
                  {selectedCategory === 'most_views' ? 'views' : 
                   selectedCategory === 'most_likes' ? 'likes' : 'views'}
                </p>
              </div>

              {/* Prize indicator for top 3 */}
              {index < 3 && (
                <div className="hidden sm:flex items-center gap-1 text-[#FFB703]">
                  <Trophy size={16} />
                </div>
              )}
            </Link>
          ))
        )}
      </div>

      {/* View All Link */}
      <Link 
        to="/leaderboard"
        className="block text-center mt-6 text-[var(--primary)] hover:underline text-sm"
      >
        View Full Leaderboard →
      </Link>
    </div>
  );
}

export function PrizeHistory({ userId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // This would fetch from an API endpoint for user's prize history
    setLoading(false);
    setHistory([]); // Demo: empty for now
  }, [userId]);

  if (loading) {
    return <div className="animate-pulse h-32 bg-[var(--surface)] rounded-xl" />;
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-8 bg-[var(--surface)] border border-[var(--border)] rounded-xl">
        <Trophy size={40} className="mx-auto text-[var(--text-secondary)] mb-3" />
        <p className="text-[var(--text-secondary)]">No prizes won yet</p>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Keep creating great content to win weekly prizes!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {history.map((prize) => (
        <div key={`prize-${prize.week_start}-${prize.category}`} className="flex items-center justify-between p-4 bg-[var(--surface)] border border-[var(--border)] rounded-lg">
          <div className="flex items-center gap-3">
            <Trophy className="text-[#FFB703]" size={24} />
            <div>
              <p className="font-medium">{prize.category}</p>
              <p className="text-sm text-[var(--text-secondary)]">
                Week of {new Date(prize.week_start).toLocaleDateString()}
              </p>
            </div>
          </div>
          <span className="text-[#FFB703] font-bold">${prize.amount}</span>
        </div>
      ))}
    </div>
  );
}

export default WeeklyPrizesBanner;
